import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  getPlatformOverview,
  listPlatformUsers,
  listAllOrganizations,
  listSubscriptions,
  listPayments,
  setUserRole,
  setUserBanned,
  deleteUser,
  setOrgActive,
  updateOrgPlan,
  createSubscription,
  cancelSubscription,
  recordPayment,
} from "@/lib/admin.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Users, Building2, CreditCard, Receipt, Ban, Trash2, Plus } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin")({
  beforeLoad: async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) throw redirect({ to: "/auth" });
    const { data: ok } = await supabase.rpc("has_role", {
      _user_id: u.user.id,
      _role: "super_admin",
    });
    if (!ok) throw redirect({ to: "/dashboard" });
  },
  component: AdminDashboard,
});

const ROLES = ["super_admin", "campaign_admin", "comms_officer", "agent", "auditor"] as const;

function money(cents: number, currency = "USD") {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format((cents || 0) / 100);
}

function AdminDashboard() {
  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Super Admin</h1>
          <p className="text-muted-foreground text-sm">Platform-wide users, organizations, subscriptions & payments.</p>
        </div>
      </div>

      <Overview />

      <Tabs defaultValue="users" className="mt-8">
        <TabsList>
          <TabsTrigger value="users"><Users className="h-4 w-4 mr-2" />Users</TabsTrigger>
          <TabsTrigger value="orgs"><Building2 className="h-4 w-4 mr-2" />Organizations</TabsTrigger>
          <TabsTrigger value="subs"><CreditCard className="h-4 w-4 mr-2" />Subscriptions</TabsTrigger>
          <TabsTrigger value="pays"><Receipt className="h-4 w-4 mr-2" />Payments</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-4"><UsersPanel /></TabsContent>
        <TabsContent value="orgs" className="mt-4"><OrgsPanel /></TabsContent>
        <TabsContent value="subs" className="mt-4"><SubsPanel /></TabsContent>
        <TabsContent value="pays" className="mt-4"><PaymentsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function Overview() {
  const fn = useServerFn(getPlatformOverview);
  const { data } = useQuery({ queryKey: ["admin-overview"], queryFn: () => fn() });
  const stats = [
    { label: "Organizations", value: data?.organizations ?? 0 },
    { label: "Users", value: data?.users ?? 0 },
    { label: "Campaigns", value: data?.campaigns ?? 0 },
    { label: "Messages sent", value: data?.messagesSent ?? 0 },
    { label: "Active subs", value: data?.activeSubscriptions ?? 0 },
    { label: "Lifetime revenue", value: money(data?.lifetimeRevenueCents ?? 0) },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
      {stats.map((s) => (
        <Card key={s.label}>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground font-medium">{s.label}</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{typeof s.value === "number" ? s.value.toLocaleString() : s.value}</div></CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---------- USERS ----------
function UsersPanel() {
  const qc = useQueryClient();
  const fn = useServerFn(listPlatformUsers);
  const setRoleFn = useServerFn(setUserRole);
  const banFn = useServerFn(setUserBanned);
  const delFn = useServerFn(deleteUser);
  const { data, isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: () => fn() });

  async function toggleRole(userId: string, role: typeof ROLES[number], has: boolean) {
    try {
      await setRoleFn({ data: { userId, role, grant: !has } });
      toast.success(`Role ${role} ${has ? "removed" : "granted"}`);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e: any) { toast.error(e.message); }
  }
  async function toggleBan(userId: string, banned: boolean) {
    try {
      await banFn({ data: { userId, banned: !banned } });
      toast.success(banned ? "User unbanned" : "User banned");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e: any) { toast.error(e.message); }
  }
  async function remove(userId: string) {
    if (!confirm("Permanently delete this user? This cannot be undone.")) return;
    try {
      await delFn({ data: { userId } });
      toast.success("User deleted");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e: any) { toast.error(e.message); }
  }

  return (
    <Card>
      <CardHeader><CardTitle>Platform users</CardTitle></CardHeader>
      <CardContent>
        {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>User</TableHead><TableHead>Email</TableHead><TableHead>Roles</TableHead>
              <TableHead>Orgs</TableHead><TableHead>Last sign-in</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data?.users ?? []).map((u: any) => {
                const banned = !!u.banned_until && new Date(u.banned_until) > new Date();
                return (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.full_name || "—"}</TableCell>
                    <TableCell className="text-xs">{u.email}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {ROLES.map((r) => {
                          const has = u.roles.includes(r);
                          return (
                            <Badge key={r} variant={has ? "default" : "outline"}
                              className="cursor-pointer text-[10px]"
                              onClick={() => toggleRole(u.id, r, has)}>
                              {r}
                            </Badge>
                          );
                        })}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{u.memberships.length}</TableCell>
                    <TableCell className="text-xs">{u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString() : "never"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant={banned ? "secondary" : "outline"} onClick={() => toggleBan(u.id, banned)}>
                        <Ban className="h-3 w-3 mr-1" />{banned ? "Unban" : "Ban"}
                      </Button>
                      <Button size="sm" variant="ghost" className="ml-1 text-destructive" onClick={() => remove(u.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- ORGS ----------
function OrgsPanel() {
  const qc = useQueryClient();
  const fn = useServerFn(listAllOrganizations);
  const toggleFn = useServerFn(setOrgActive);
  const updateFn = useServerFn(updateOrgPlan);
  const { data, isLoading } = useQuery({ queryKey: ["admin-orgs"], queryFn: () => fn() });
  const [editing, setEditing] = useState<any | null>(null);

  async function toggle(orgId: string, active: boolean) {
    try {
      await toggleFn({ data: { orgId, active: !active } });
      toast.success(active ? "Organization deactivated" : "Organization activated");
      qc.invalidateQueries({ queryKey: ["admin-orgs"] });
    } catch (e: any) { toast.error(e.message); }
  }

  return (
    <Card>
      <CardHeader><CardTitle>All organizations</CardTitle></CardHeader>
      <CardContent>
        {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead>
              <TableHead>Quota</TableHead><TableHead>Renews</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data?.organizations ?? []).map((o: any) => (
                <TableRow key={o.id}>
                  <TableCell className="font-medium">{o.name}<div className="text-xs text-muted-foreground">{o.slug}</div></TableCell>
                  <TableCell><Badge variant="outline">{o.subscription_plan}</Badge></TableCell>
                  <TableCell><Badge>{o.subscription_status}</Badge></TableCell>
                  <TableCell className="text-xs">{o.monthly_message_quota?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs">{o.subscription_renews_at ? new Date(o.subscription_renews_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell>{o.is_active ? <Badge>Active</Badge> : <Badge variant="destructive">Inactive</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant={o.is_active ? "outline" : "default"} onClick={() => toggle(o.id, o.is_active)}>
                      {o.is_active ? "Deactivate" : "Activate"}
                    </Button>
                    <Button size="sm" variant="ghost" className="ml-1" onClick={() => setEditing(o)}>Edit plan</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <EditPlanDialog org={editing} onClose={() => setEditing(null)} updateFn={updateFn}
          onSaved={() => qc.invalidateQueries({ queryKey: ["admin-orgs"] })} />
      </CardContent>
    </Card>
  );
}

function EditPlanDialog({ org, onClose, updateFn, onSaved }: any) {
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [quota, setQuota] = useState(0);
  const [renews, setRenews] = useState("");

  useEffect(() => {
    if (org) {
      setPlan(org.subscription_plan);
      setStatus(org.subscription_status);
      setQuota(org.monthly_message_quota);
      setRenews(org.subscription_renews_at ? new Date(org.subscription_renews_at).toISOString().slice(0, 16) : "");
    }
  }, [org]);

  async function save() {
    try {
      await updateFn({
        data: {
          orgId: org.id, plan, status, monthly_message_quota: Number(quota),
          renews_at: renews ? new Date(renews).toISOString() : null,
        },
      });
      toast.success("Plan updated");
      onSaved(); onClose();
    } catch (e: any) { toast.error(e.message); }
  }

  return (
    <Dialog open={!!org} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit plan — {org?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Plan</Label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["free", "starter", "growth", "enterprise"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["trialing", "active", "past_due", "canceled", "suspended"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Monthly message quota</Label><Input type="number" value={quota} onChange={(e) => setQuota(Number(e.target.value))} /></div>
          <div><Label>Renews at</Label><Input type="datetime-local" value={renews} onChange={(e) => setRenews(e.target.value)} /></div>
          <Button onClick={save} className="w-full">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- SUBSCRIPTIONS ----------
function SubsPanel() {
  const qc = useQueryClient();
  const fn = useServerFn(listSubscriptions);
  const orgsFn = useServerFn(listAllOrganizations);
  const createFn = useServerFn(createSubscription);
  const cancelFn = useServerFn(cancelSubscription);
  const { data, isLoading } = useQuery({ queryKey: ["admin-subs"], queryFn: () => fn() });
  const { data: orgsData } = useQuery({ queryKey: ["admin-orgs"], queryFn: () => orgsFn() });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ orgId: "", plan: "starter", status: "active", monthly_message_quota: 10000, price_cents: 4900, currency: "USD", period_end: "", notes: "" });

  async function submit() {
    try {
      await createFn({ data: { ...form, period_end: form.period_end ? new Date(form.period_end).toISOString() : null } });
      toast.success("Subscription created");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-subs"] });
      qc.invalidateQueries({ queryKey: ["admin-orgs"] });
    } catch (e: any) { toast.error(e.message); }
  }
  async function cancel(id: string) {
    if (!confirm("Cancel this subscription?")) return;
    try {
      await cancelFn({ data: { subscriptionId: id } });
      toast.success("Subscription canceled");
      qc.invalidateQueries({ queryKey: ["admin-subs"] });
    } catch (e: any) { toast.error(e.message); }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Subscriptions</CardTitle>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" />New subscription</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create subscription</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Organization</Label>
                <Select value={form.orgId} onValueChange={(v) => setForm({ ...form, orgId: v })}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {(orgsData?.organizations ?? []).map((o: any) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Plan</Label><Input value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} /></div>
                <div><Label>Status</Label><Input value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} /></div>
                <div><Label>Quota</Label><Input type="number" value={form.monthly_message_quota} onChange={(e) => setForm({ ...form, monthly_message_quota: Number(e.target.value) })} /></div>
                <div><Label>Price (cents)</Label><Input type="number" value={form.price_cents} onChange={(e) => setForm({ ...form, price_cents: Number(e.target.value) })} /></div>
                <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} maxLength={3} /></div>
                <div><Label>Period end</Label><Input type="datetime-local" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} /></div>
              </div>
              <Button className="w-full" disabled={!form.orgId} onClick={submit}>Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Org</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead>
              <TableHead>Quota</TableHead><TableHead>Price</TableHead><TableHead>Period</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data?.subscriptions ?? []).map((s: any) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.organizations?.name}</TableCell>
                  <TableCell><Badge variant="outline">{s.plan}</Badge></TableCell>
                  <TableCell><Badge>{s.status}</Badge></TableCell>
                  <TableCell className="text-xs">{s.monthly_message_quota?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs">{money(s.price_cents, s.currency)}</TableCell>
                  <TableCell className="text-xs">
                    {new Date(s.period_start).toLocaleDateString()}
                    {s.period_end ? ` → ${new Date(s.period_end).toLocaleDateString()}` : ""}
                  </TableCell>
                  <TableCell className="text-right">
                    {s.status !== "canceled" && <Button size="sm" variant="outline" onClick={() => cancel(s.id)}>Cancel</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- PAYMENTS ----------
function PaymentsPanel() {
  const qc = useQueryClient();
  const fn = useServerFn(listPayments);
  const orgsFn = useServerFn(listAllOrganizations);
  const recordFn = useServerFn(recordPayment);
  const { data, isLoading } = useQuery({ queryKey: ["admin-payments"], queryFn: () => fn() });
  const { data: orgsData } = useQuery({ queryKey: ["admin-orgs"], queryFn: () => orgsFn() });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ orgId: "", amount_cents: 4900, currency: "USD", status: "succeeded" as const, provider: "manual", provider_reference: "", notes: "" });

  async function submit() {
    try {
      await recordFn({ data: form });
      toast.success("Payment recorded");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-payments"] });
    } catch (e: any) { toast.error(e.message); }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Payments</CardTitle>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" />Record payment</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Organization</Label>
                <Select value={form.orgId} onValueChange={(v) => setForm({ ...form, orgId: v })}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {(orgsData?.organizations ?? []).map((o: any) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount (cents)</Label><Input type="number" value={form.amount_cents} onChange={(e) => setForm({ ...form, amount_cents: Number(e.target.value) })} /></div>
                <div><Label>Currency</Label><Input value={form.currency} maxLength={3} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></div>
                <div><Label>Provider</Label><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} /></div>
                <div><Label>Reference</Label><Input value={form.provider_reference} onChange={(e) => setForm({ ...form, provider_reference: e.target.value })} /></div>
              </div>
              <Button className="w-full" disabled={!form.orgId} onClick={submit}>Record</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Org</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead>
              <TableHead>Provider</TableHead><TableHead>Ref</TableHead><TableHead>Paid</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data?.payments ?? []).map((p: any) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.organizations?.name}</TableCell>
                  <TableCell>{money(p.amount_cents, p.currency)}</TableCell>
                  <TableCell><Badge variant={p.status === "succeeded" ? "default" : "outline"}>{p.status}</Badge></TableCell>
                  <TableCell className="text-xs">{p.provider}</TableCell>
                  <TableCell className="text-xs">{p.provider_reference || "—"}</TableCell>
                  <TableCell className="text-xs">{new Date(p.paid_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
